import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqListSource,
    SortDirectionEnterprise,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import {
    ClassNames,
    Color,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    Icons,
    Interaction,
    Modal,
    ModalService,
    TabsConfig
} from '@lsq-frontend/common-ui';
import { FastTrackRouteQueryParams } from '@lsq-frontend/fasttrack-shared/app-constants';
import { ADMIN_PURCHASE_REQUESTS, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    Money,
    PurchaseRequestStatus,
    UpdatePurchaseRequestService
} from '@lsq-frontend/remote-services/funding-api';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { MarkPurchaseRequestModalComponent } from './mark-purchase-request-modal/mark-purchase-request-modal.component';
import {
    NewPurchaseRequestsModalComponent,
    PurchaseRequestValidationError
} from './new-purchase-requests-modal/new-purchase-requests-modal.component';

interface PurchaseRequest {
    id: string;
    buyerName: string;
    funderCompanyNames: string;
    totalFunderCapital: Money;
    sold: boolean;
    soldDate: string;
}

const GET_PURCHASE_REQUESTS = gql`
    query purchaseRequests(
        $search: String
        $skip: Int!
        $take: Int!
        $sortColumn: PurchaseRequestsSortColumn!
        $sortDirection: SortDirection!
    ) {
        purchaseRequests(
            input: {
                purchaseRequestIdSearch: $search
                pagination: {
                    skip: $skip
                    take: $take
                    sortColumn: $sortColumn
                    sortDirection: $sortDirection
                    secondarySortColumn: PURCHASE_REQUEST_ID
                    secondarySortDirection: $sortDirection
                }
            }
        ) {
            rows {
                id
                status
                soldDate
                totalFunderCapital {
                    cents
                    currencyCode
                }
                buyer {
                    id
                    company {
                        name
                    }
                }
                funders {
                    company {
                        id
                        name
                        legalName
                    }
                }
            }
            totalCount
        }
    }
`;

@Component({
    selector: 'admin-purchase-requests',
    styleUrls: ['./purchase-requests.component.scss'],
    templateUrl: './purchase-requests.component.html'
})
export class AdminPurchaseRequestsComponent implements OnInit {
    public readonly color = Color;
    public readonly icons = Icons;
    public routeQueryParams = FastTrackRouteQueryParams;

    public toastMessage: string;

    public readonly tabsConfig: TabsConfig[] = [
        {
            label: this._i18n('Uploads'),
            routerLink: getDynamicRouteLink(
                getRouterLinkUrlById(atlas, ADMIN_PURCHASE_REQUESTS),
                {}
            ),
            queryParams: {
                tab: FastTrackRouteQueryParams.PurchaseRequestsUploads
            }
        },
        {
            label: this._i18n('Existing Requests'),
            routerLink: getDynamicRouteLink(
                getRouterLinkUrlById(atlas, ADMIN_PURCHASE_REQUESTS),
                {}
            ),
            queryParams: {
                tab: FastTrackRouteQueryParams.PurchaseRequestsExistingRequests
            }
        }
    ];

    public readonly purchaseRequestsFormGroup = new FormGroup({
        purchaseRequestId: new FormControl()
    });

    private readonly initialSkip = 0;
    private readonly initialTake = 25;
    private readonly initialSortColumn = 'SOLD_DATE';
    private readonly initialSortDirection = SortDirectionFinancing.DESCENDING;

    private purchaseRequestsQuery = this._apollo.use('financingGraphQl').watchQuery<any>({
        query: GET_PURCHASE_REQUESTS,
        variables: {
            skip: this.initialSkip,
            take: this.initialTake,
            sortColumn: this.initialSortColumn,
            sortDirection: this.initialSortDirection
        }
    });

    public validationErrorsDataSource: LsqListSource<PurchaseRequestValidationError>;
    public purchaseRequestsDataSource: LsqListSource<any>;
    private validationErrorsSubject = new Subject<PurchaseRequestValidationError[]>();

    public readonly noValidationErrorsI18n = this._i18n(`
       You have no Purchase Request Errors. Any purchase request errors from files that you upload
       will be here.`);
    public readonly searchPlaceholderI18n = this._i18n('Search Purchase ID');

    // Table headers
    public readonly buyerNameI18n = this._i18n('Buyer Name');
    public readonly supplierNameI18n = this._i18n('Supplier Name');
    public readonly funderCompanyNamesI18n = this._i18n('Funder Company Name(s)');
    public readonly invoiceIdI18n = this._i18n('Invoice ID');
    public readonly invoiceAmountI18n = this._i18n('Invoice Amount');
    public readonly errorMessageI18n = this._i18n('Error Message');
    public readonly purchaseIdI18n = this._i18n('Purchase ID');
    public readonly markAsSoldI18n = this._i18n('Mark as Sold');
    public readonly dateSoldI18n = this._i18n('Date Sold');
    public readonly totalFunderCapitalI18n = this._i18n('Total Funder Capital');

    // Modal
    private readonly markAsSoldModalTitleI18n = this._i18n('Mark Purchase Request as Sold');
    private readonly markAsPendingModalTitleI18n = this._i18n('Mark Purchase Request as Pending');
    private readonly markAsPendingI18n = this._i18n('Mark as Pending');

    private readonly successUpdatedI18n = this._i18n('Purchase request successfully updated');

    private readonly validationErrorMessage = this._i18n(
        'Errors found in upload. Review error messages and reupload file(s) to resolve.'
    );
    private readonly successMessage = this._i18n(
        'File uploaded successfully. Review successful uploads in the "Existing Requests" tab.'
    );

    public activeTabLabel$ = this._activatedRoute.queryParams.pipe(
        map((queryParams) => queryParams.tab)
    );

    @ViewChild('createPurchaseRequestsModal', { read: TemplateRef })
    private _createPurchaseRequestsModal!: TemplateRef<NewPurchaseRequestsModalComponent>;

    private readonly keyToSortColumn = new Map([
        ['id', 'PURCHASE_REQUEST_ID'],
        ['soldDate', 'SOLD_DATE']
    ]);

    private readonly sortColumnToKey = new Map([
        ['PURCHASE_REQUEST_ID', 'id'],
        ['SOLD_DATE', 'soldDate']
    ]);

    constructor(
        private _i18n: I18n,
        private _modalService: ModalService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _apollo: Apollo,
        private _updatePurchaseRequestService: UpdatePurchaseRequestService,
        private _alertService: AlertService
    ) {
        this._activatedRoute.queryParams.subscribe((queryParams) => {
            if (queryParams.tab) {
                return;
            }
            // Set the default tab's query param by default if none selected.
            this._router.navigate([], {
                relativeTo: this._activatedRoute,
                queryParams: { tab: FastTrackRouteQueryParams.PurchaseRequestsUploads },
                replaceUrl: true
            });
        });
    }

    ngOnInit(): void {
        this.validationErrorsDataSource = new LsqListSource({
            rows$: this.validationErrorsSubject
        });
        this.validationErrorsSubject.next([]);

        const loading$ = new BehaviorSubject(false);
        const skip$ = new BehaviorSubject(this.initialSkip);
        const take$ = new BehaviorSubject(this.initialTake);
        const sort$ = new BehaviorSubject([this.initialSortColumn, this.initialSortDirection]);

        const totalCount$ = new BehaviorSubject(0);

        const rows$ = this.purchaseRequestsQuery.valueChanges.pipe(
            map(({ data, loading }) => {
                if (loading) {
                    loading$.next(true);
                }
                if (!data) {
                    return;
                }

                loading$.next(false);
                totalCount$.next(data.purchaseRequests.totalCount);
                const rows = data.purchaseRequests.rows;
                return rows.map((row: any) => {
                    return {
                        buyerName: row.buyer.company.name,
                        funderCompanyNames: row.funders.map((f: any) => f.company.name).join(', '),
                        id: row.id,
                        totalFunderCapital: row.totalFunderCapital,
                        sold: !!row.soldDate,
                        soldDate: row.soldDate
                    } as PurchaseRequest;
                });
            })
        );

        this.purchaseRequestsDataSource = new LsqListSource<PurchaseRequest>({
            rows$,
            totalCount$,
            loading$,
            take: this.initialTake,
            skip: this.initialSkip,
            sortColumn: this.sortColumnToKey.get(this.initialSortColumn),
            sortDirection: this.initialSortDirection,
            handleSort: (sorting: { sortColumn: string; sortDirection: any }) => {
                const col = this.keyToSortColumn.get(sorting.sortColumn);
                const dir =
                    sorting.sortDirection == SortDirectionEnterprise.Asc
                        ? SortDirectionFinancing.ASCENDING
                        : SortDirectionFinancing.DESCENDING;
                sort$.next([col, dir]);

                this.purchaseRequestsDataSource.sortColumn = sorting.sortColumn;
                this.purchaseRequestsDataSource.sortDirection = sorting.sortDirection;
            },
            handleSkipChange: (value) => {
                this.purchaseRequestsDataSource.skip = value;
                skip$.next(value);
            },
            handleTakeChange: (value) => {
                this.purchaseRequestsDataSource.take = value;
                take$.next(value);
            }
        });

        const search$ = this.purchaseRequestsFormGroup.valueChanges.pipe(
            startWith(''),
            distinctUntilChanged(),
            debounceTime(Interaction.SearchDebounceTimeMs),
            map((filter: { purchaseRequestId: string }) => filter.purchaseRequestId)
        );

        combineLatest([search$, skip$, take$, sort$]).subscribe(
            ([search, skip, take, [sortColumn, sortDirection]]) => {
                this.purchaseRequestsQuery.setVariables({
                    search,
                    skip,
                    take,
                    sortColumn,
                    sortDirection
                });
            }
        );

        this.activeTabLabel$.subscribe((tab) => {
            if (tab == this.routeQueryParams.PurchaseRequestsExistingRequests) {
                this.purchaseRequestsQuery.refetch();
            }
        });
    }

    openNewPurchaseRequestsModal() {
        const purchaseRequestUploadModal = new Modal({
            title: this._i18n('Upload Purchase Requests'),
            id: 'new-purchase-requests',
            showCloseButton: true,
            templateRef: this._createPurchaseRequestsModal
        });
        this._modalService.showModal(purchaseRequestUploadModal);
    }

    openMarkPurchaseRequestModal(event: Event, purchaseRequest: PurchaseRequest) {
        event.preventDefault();

        const newStatus = purchaseRequest.soldDate
            ? PurchaseRequestStatus.PENDING_SALE
            : PurchaseRequestStatus.SOLD;

        const isSubmittingSubject$ = new BehaviorSubject<boolean>(false);
        const soldDate$ = new BehaviorSubject<string>(null);
        const data = {
            purchaseRequest,
            soldDate$
        };

        const markPurchaseRequestModal = new Modal({
            title:
                newStatus == PurchaseRequestStatus.PENDING_SALE
                    ? this.markAsPendingModalTitleI18n
                    : this.markAsSoldModalTitleI18n,
            id: 'mark-purchase-request',
            showCloseButton: true,
            bodyComponent: MarkPurchaseRequestModalComponent,
            showOkButton: true,
            showCancelButton: true,
            showFooter: true,
            okButtonSize: ClassNames.Block,
            okButtonText:
                newStatus == PurchaseRequestStatus.PENDING_SALE
                    ? this.markAsPendingI18n
                    : this.markAsSoldI18n,
            okButtonIsDisabled$: soldDate$.pipe(
                map((val) => newStatus == PurchaseRequestStatus.SOLD && !val)
            ),
            isSubmitting$: isSubmittingSubject$,
            data,
            onOkClick: async () => {
                isSubmittingSubject$.next(true);
                try {
                    await this._updatePurchaseRequestService.executeOnce({
                        purchaseRequestId: purchaseRequest.id,
                        status: newStatus,
                        soldDate: data.soldDate$.getValue()
                    });

                    isSubmittingSubject$.next(false);
                    this._alertService.success(this.successUpdatedI18n);
                } finally {
                    this._modalService.closeModal();
                }
                this.purchaseRequestsQuery.refetch();
            }
        });
        this._modalService.showModal(markPurchaseRequestModal);
    }

    onValidationErrors(errs: PurchaseRequestValidationError[]) {
        this.toastMessage = this.validationErrorMessage;
        this.validationErrorsSubject.next(errs);
        this._modalService.closeModal();
    }

    onSuccessfulUpload() {
        this.toastMessage = this.successMessage;
        this.validationErrorsSubject.next([]);
        this._modalService.closeModal();
    }
}
